import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import {AddOutlined, DeleteOutline} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from '../../../../../../../core/models/static/texts/vallidate-messages';
import {categoryOptions, conditionFieldOptions, dayOfWeekOptions, formulaOptions, mockCustomers, paymentMethodOptions,} from '../../mock-data';

const formNames = {
    name: 'name',
    formula: 'formula',
    formulaAmount: 'formulaAmount',
    dailyCap: 'dailyCap',
    monthlyCap: 'monthlyCap',
    conditions: 'conditions',
};

const createEmptyCondition = () => ({field: 'TotalAmount', operator: 'GreaterThan', value: ''});
const createEmptyGroup = () => ({type: 'AND', children: [createEmptyCondition()]});

const operatorsByField = {
    TotalAmount: ['GreaterThan', 'LessThan', 'Equals', 'Between'],
    PaymentMethod: ['Equals', 'In'],
    'LineItem.SkuOrCode': ['Equals', 'In', 'Contains'],
    'LineItem.CategoryCode': ['Equals', 'In'],
    TransactionDate: ['DateBetween', 'DayOfWeek'],
    Customer: ['Equals', 'In'],
    DayOfWeek: ['In'],
};

const buildDefaultValues = (rule) => {
    if (rule) {
        return {
            id: rule.id,
            ver: rule.ver,
            active: rule.active,
            versions: rule.versions ?? [],
            [formNames.name]: rule.name ?? '',
            [formNames.formula]: rule.formula ?? 'PerDollar',
            [formNames.formulaAmount]: rule.formulaAmount ?? 10,
            [formNames.dailyCap]: rule.dailyCap ?? 500,
            [formNames.monthlyCap]: rule.monthlyCap ?? 5000,
            [formNames.conditions]: rule.versions?.[rule.versions.length - 1]?.conditions
                ?? rule.conditions
                ?? createEmptyGroup(),
        };
    }
    return {
        id: `rule-${Date.now()}`,
        ver: 1,
        active: false,
        versions: [],
        [formNames.name]: '',
        [formNames.formula]: 'PerDollar',
        [formNames.formulaAmount]: 10,
        [formNames.dailyCap]: 500,
        [formNames.monthlyCap]: 5000,
        [formNames.conditions]: createEmptyGroup(),
    };
};

const ConditionRow = ({condition, onChange, onRemove, t, nestedLevel = 0, allowRemove = true}) => {
    const operators = operatorsByField[condition.field] ?? ['Equals'];
    const isGroup = condition.type === 'AND' || condition.type === 'OR';

    if (isGroup) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    border: '1px dashed',
                    borderColor: 'secondary.main',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.paper',
                    ml: nestedLevel > 0 ? 2 : 0,
                    '&:before': nestedLevel > 0
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: -16,
                            top: 20,
                            width: 16,
                            borderTop: '1px dashed',
                            borderColor: 'secondary.main',
                        }
                        : undefined,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1.5}}>
                    <FormControl size="small" sx={{minWidth: 140}}>
                        <InputLabel>{t('groupType')}</InputLabel>
                        <Select
                            label={t('groupType')}
                            value={condition.type}
                            onChange={(e) => onChange({...condition, type: e.target.value})}
                        >
                            <MenuItem value="AND">{t('and')}</MenuItem>
                            <MenuItem value="OR">{t('or')}</MenuItem>
                        </Select>
                    </FormControl>
                    {allowRemove && (
                        <IconButton size="small" color="error" onClick={onRemove} aria-label={t('remove')}>
                            <DeleteOutline fontSize="small"/>
                        </IconButton>
                    )}
                </Stack>
                <Stack spacing={1.5}>
                    {condition.children?.map((child, idx) => (
                        <ConditionRow
                            key={idx}
                            condition={child}
                            t={t}
                            nestedLevel={nestedLevel + 1}
                            onChange={(updated) => {
                                const children = [...condition.children];
                                children[idx] = updated;
                                onChange({...condition, children});
                            }}
                            onRemove={() => {
                                const children = condition.children.filter((_, i) => i !== idx);
                                onChange({...condition, children});
                            }}
                        />
                    ))}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddOutlined/>}
                            onClick={() => {
                                onChange({
                                    ...condition,
                                    children: [...(condition.children ?? []), createEmptyCondition()],
                                });
                            }}
                        >
                            {t('addCondition')}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddOutlined/>}
                            onClick={() => {
                                onChange({
                                    ...condition,
                                    children: [...(condition.children ?? []), createEmptyGroup()],
                                });
                            }}
                        >
                            {t('addGroup')}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        );
    }

    const renderValueInput = () => {
        if (condition.field === 'DayOfWeek' || (condition.field === 'TransactionDate' && condition.operator === 'DayOfWeek')) {
            return (
                <Autocomplete
                    multiple
                    size="small"
                    options={dayOfWeekOptions}
                    value={Array.isArray(condition.value) ? condition.value : []}
                    onChange={(_, val) => onChange({...condition, value: val})}
                    renderTags={(val, getTagProps) =>
                        val.map((option, index) => (
                            <Chip {...getTagProps({index})} key={option} label={option} size="small"/>
                        ))
                    }
                    renderInput={(params) => <TextField {...params} label={t('value')}/>}
                    sx={{minWidth: 240}}
                />
            );
        }
        if (condition.operator === 'DateBetween') {
            const [start = '', end = ''] = Array.isArray(condition.value) ? condition.value : ['', ''];
            return (
                <Stack direction="row" spacing={1}>
                    <TextField
                        size="small"
                        type="date"
                        label={t('startDate')}
                        value={start}
                        onChange={(e) => onChange({...condition, value: [e.target.value, end]})}
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label={t('endDate')}
                        value={end}
                        onChange={(e) => onChange({...condition, value: [start, e.target.value]})}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>
            );
        }
        if (condition.field === 'PaymentMethod') {
            return (
                <Autocomplete
                    freeSolo
                    size="small"
                    options={paymentMethodOptions}
                    value={condition.value ?? ''}
                    onChange={(_, val) => onChange({...condition, value: val ?? ''})}
                    onInputChange={(_, val) => onChange({...condition, value: val})}
                    renderInput={(params) => <TextField {...params} label={t('value')}/>}
                    sx={{minWidth: 180}}
                />
            );
        }
        if (condition.field === 'LineItem.CategoryCode') {
            return (
                <Autocomplete
                    freeSolo
                    size="small"
                    options={categoryOptions}
                    value={condition.value ?? ''}
                    onChange={(_, val) => onChange({...condition, value: val ?? ''})}
                    onInputChange={(_, val) => onChange({...condition, value: val})}
                    renderInput={(params) => <TextField {...params} label={t('value')}/>}
                    sx={{minWidth: 180}}
                />
            );
        }
        if (condition.field === 'Customer') {
            const customerOptions = mockCustomers.map((c) => `${c.name} (#${c.customerNo})`);
            return (
                <Autocomplete
                    freeSolo
                    size="small"
                    options={customerOptions}
                    value={condition.value ?? ''}
                    onChange={(_, val) => onChange({...condition, value: val ?? ''})}
                    onInputChange={(_, val) => onChange({...condition, value: val})}
                    renderInput={(params) => <TextField {...params} label={t('value')}/>}
                    sx={{minWidth: 220}}
                />
            );
        }
        return (
            <TextField
                size="small"
                label={t('value')}
                type={condition.field === 'TotalAmount' ? 'number' : 'text'}
                value={condition.value ?? ''}
                onChange={(e) => onChange({...condition, value: e.target.value})}
                sx={{minWidth: 120}}
            />
        );
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                ml: nestedLevel > 0 ? 1 : 0,
                '&:before': nestedLevel > 0
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: -12,
                        top: '50%',
                        width: 12,
                        borderTop: '1px dashed',
                        borderColor: 'secondary.main',
                    }
                    : undefined,
            }}
        >
            <FormControl size="small" sx={{minWidth: 160}}>
                <InputLabel>{t('field')}</InputLabel>
                <Select
                    label={t('field')}
                    value={condition.field}
                    onChange={(e) => onChange({
                        ...condition,
                        field: e.target.value,
                        operator: operatorsByField[e.target.value]?.[0] ?? 'Equals',
                        value: '',
                    })}
                >
                    {conditionFieldOptions.map((f) => (
                        <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" sx={{minWidth: 130}}>
                <InputLabel>{t('operator')}</InputLabel>
                <Select
                    label={t('operator')}
                    value={condition.operator}
                    onChange={(e) => onChange({...condition, operator: e.target.value})}
                >
                    {operators.map((op) => (
                        <MenuItem key={op} value={op}>{op}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {renderValueInput()}
            {allowRemove && (
                <IconButton size="small" color="error" onClick={onRemove} aria-label={t('remove')}>
                    <DeleteOutline fontSize="small"/>
                </IconButton>
            )}
        </Box>
    );
};

const RuleBuilderDialog = ({open, rule, onClose, onSave}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.rules.builder'});
    const submitIntentRef = useRef('draft');
    const [formKey, setFormKey] = useState(0);
    const [preview, setPreview] = useState({
        amount: 150,
        paymentMethod: 'Visa',
        dayOfWeek: ['Saturday'],
        category: 'TOOLS',
        customer: 'Jane Smith (#1042)',
    });
    const [testResult, setTestResult] = useState(null);
    const valuesRef = useRef(buildDefaultValues(null));

    /**
     * Rebuilds form initial values when the dialog opens.
     */
    useEffect(() => {
        if (open) {
            const next = buildDefaultValues(rule);
            setFormKey((k) => k + 1);
            valuesRef.current = next;
            setTestResult(null);
            submitIntentRef.current = 'draft';
        }
    }, [open, rule]);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.name]: Yup.string().trim().nullable().required(ValidateMessages.required),
        [formNames.formula]: Yup.string().required(ValidateMessages.required),
        [formNames.formulaAmount]: Yup.number().typeError(ValidateMessages.incorrectType('number')).min(0).required(ValidateMessages.required),
        [formNames.dailyCap]: Yup.number().typeError(ValidateMessages.incorrectType('number')).moreThan(0, ValidateMessages.required).required(ValidateMessages.required),
        [formNames.monthlyCap]: Yup.number().typeError(ValidateMessages.incorrectType('number')).moreThan(0, ValidateMessages.required).required(ValidateMessages.required),
        [formNames.conditions]: Yup.mixed().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => buildDefaultValues(rule), [formKey, rule]);

    /**
     * Runs mock rule preview against sample transaction fields.
     */
    const handleTest = useCallback(async () => {
        const current = valuesRef.current;
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({
            resultFlag: true,
            data: {
                matchedRules: [
                    {
                        name: current[formNames.name] || t('unnamedRule'),
                        biz: Math.min(Number(current[formNames.formulaAmount]) * 3, Number(current[formNames.dailyCap])),
                    },
                ],
                capped: preview.amount > Number(current[formNames.dailyCap]),
            },
        });
        if (response?.resultFlag) {
            setTestResult(response.data);
        }
    }, [preview.amount, t]);

    /**
     * Saves draft or activates based on submit intent.
     */
    const handleSubmit = useCallback((values) => {
        const activate = submitIntentRef.current === 'activate';
        const payload = {
            id: values.id,
            ver: values.ver,
            active: values.active,
            versions: values.versions,
            name: values[formNames.name]?.trim(),
            formula: values[formNames.formula],
            formulaAmount: Number(values[formNames.formulaAmount]),
            dailyCap: Number(values[formNames.dailyCap]),
            monthlyCap: Number(values[formNames.monthlyCap]),
            conditions: values[formNames.conditions],
            caps: `${values[formNames.dailyCap]} / ${values[formNames.monthlyCap]}`,
            condSummary: values[formNames.name]
                ? `${values[formNames.name].trim()} conditions`
                : t('draftConditions'),
        };
        onSave?.(payload, activate);
    }, [onSave, t]);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="lg"
            className="bound-dialog"
        >
            <DialogTitle component="div">
                <Typography variant="h6" sx={{p: 2}}>{rule ? t('editTitle') : t('newTitle')}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    key={formKey}
                    onSubmit={handleSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'biz-point-rule-builder-form'}}
                >
                    {({values, form}) => {
                        valuesRef.current = values;
                        return (
                            <Grid container spacing={3} sx={{p: 3, m: 0}}>
                                <Grid xs={12} md={7}>
                                    <Stack spacing={1.5} sx={{height: '100%'}}>
                                        <Typography variant="subtitle2">{t('conditions')}</Typography>
                                        <ConditionRow
                                            condition={values[formNames.conditions]}
                                            t={t}
                                            allowRemove={false}
                                            onChange={(conditions) => form.change(formNames.conditions, conditions)}
                                            onRemove={() => {
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid xs={12} md={5}>
                                    <Stack
                                        spacing={2.5}
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                            position: {md: 'sticky'},
                                            top: {md: 8},
                                        }}
                                    >
                                        <MuiRFF.TextField
                                            name={formNames.name}
                                            label={t('ruleName')}
                                            required={required[formNames.name]}
                                            fullWidth
                                        />
                                        <MuiRFF.Select
                                            name={formNames.formula}
                                            label={t('formula')}
                                            required={required[formNames.formula]}
                                            data={formulaOptions.map((f) => ({label: f, value: f}))}
                                            formControlProps={{fullWidth: true}}
                                        />
                                        <MuiRFF.TextField
                                            name={formNames.formulaAmount}
                                            label={t('formulaAmount')}
                                            type="number"
                                            required={required[formNames.formulaAmount]}
                                            fullWidth
                                        />
                                        <MuiRFF.TextField
                                            name={formNames.dailyCap}
                                            label={t('dailyCap')}
                                            type="number"
                                            required={required[formNames.dailyCap]}
                                            helperText={t('capRequired')}
                                            fullWidth
                                        />
                                        <MuiRFF.TextField
                                            name={formNames.monthlyCap}
                                            label={t('monthlyCap')}
                                            type="number"
                                            required={required[formNames.monthlyCap]}
                                            helperText={t('capRequired')}
                                            fullWidth
                                        />
                                        <Divider/>
                                        <Typography variant="subtitle2">{t('previewTest')}</Typography>
                                        <TextField
                                            label={t('amount')}
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={preview.amount}
                                            onChange={(e) => setPreview((p) => ({...p, amount: Number(e.target.value)}))}
                                        />
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={paymentMethodOptions}
                                            value={preview.paymentMethod}
                                            onChange={(_, val) => setPreview((p) => ({...p, paymentMethod: val ?? ''}))}
                                            renderInput={(params) => <TextField {...params} label={t('paymentMethod')}/>}
                                        />
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={dayOfWeekOptions}
                                            value={preview.dayOfWeek}
                                            onChange={(_, val) => setPreview((p) => ({...p, dayOfWeek: val}))}
                                            renderInput={(params) => <TextField {...params} label={t('dayOfWeek')}/>}
                                        />
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={categoryOptions}
                                            value={preview.category}
                                            onChange={(_, val) => setPreview((p) => ({...p, category: val ?? ''}))}
                                            renderInput={(params) => <TextField {...params} label={t('category')}/>}
                                        />
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={mockCustomers.map((c) => `${c.name} (#${c.customerNo})`)}
                                            value={preview.customer}
                                            onChange={(_, val) => setPreview((p) => ({...p, customer: val ?? ''}))}
                                            renderInput={(params) => <TextField {...params} label={t('customer')}/>}
                                        />
                                        <Button variant="outlined" onClick={handleTest}>{t('test')}</Button>
                                        {testResult && (
                                            <Box sx={{bgcolor: 'action.hover', p: 2, borderRadius: 1}}>
                                                {testResult.matchedRules.map((mr) => (
                                                    <Typography key={mr.name} variant="body2">
                                                        {t('matchResult', {name: mr.name, biz: mr.biz})}
                                                    </Typography>
                                                ))}
                                                {testResult.capped && (
                                                    <Typography variant="caption" color="warning.main">{t('capApplied')}</Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        );
                    }}
                </Form>
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button variant="dialog" color="primary" borderRadius="0 8px 0 8px" onClick={onClose}>
                    {t('cancel')}
                </Button>
                <Box sx={{display: 'flex'}}>
                    <Button
                        variant="dialog"
                        color="primary"
                        type="submit"
                        form="biz-point-rule-builder-form"
                        borderRadius="8px 0 0 0"
                        onClick={() => {
                            submitIntentRef.current = 'draft';
                        }}
                    >
                        {t('saveDraft')}
                    </Button>
                    <Button
                        variant="dialog"
                        color="primary"
                        borderRadius="0 0 8px 0"
                        type="submit"
                        form="biz-point-rule-builder-form"
                        onClick={() => {
                            submitIntentRef.current = 'activate';
                        }}
                    >
                        {t('activate')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default RuleBuilderDialog;
